'use client';

import { useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import posthog from 'posthog-js';
import { clearSignupDistinctIdCookie, ensurePostHogInitialized } from '@/lib/client-analytics';

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN || process.env.NEXT_PUBLIC_POSTHOG_KEY;

interface SessionUser {
  email?: string | null;
  id?: string;
  name?: string | null;
}

export function PostHogUserIdentification() {
  const { data: session, status } = useSession();
  const lastIdentifiedIdRef = useRef<string | null>(null);
  const user = session?.user as SessionUser | undefined;

  useEffect(() => {
    if (!posthogKey || !ensurePostHogInitialized()) {
      return;
    }

    if (status === 'authenticated' && user?.id) {
      // identify already stitches anonymous activity; never alias two signed-in accounts.
      const previousUser = posthog.get_property('$user_id');
      if (previousUser && previousUser !== user.id) posthog.reset();

      if (lastIdentifiedIdRef.current === user.id) {
        return;
      }

      posthog.identify(user.id, {
        email: user.email ?? undefined,
        name: user.name ?? undefined,
      });
      clearSignupDistinctIdCookie();
      lastIdentifiedIdRef.current = user.id;
      return;
    }

    if (status === 'unauthenticated' && (lastIdentifiedIdRef.current || posthog.get_property('$user_id'))) {
      posthog.reset();
      lastIdentifiedIdRef.current = null;
    }
  }, [status, user?.email, user?.id, user?.name]);

  return null;
}
